<template>
  <v-container fluid pa-0 mt-0 pt-1>
    <v-row align="center">
      <v-col class="py-0">
        <itinerary-builder-settings></itinerary-builder-settings>
      </v-col>
      <v-col class="py-0">
        <v-container py-0>
          <v-row justify="end">
            <list-visible-columns-and-filter
              :visible-columns="visibleColumns"
              :selectable-headers="headers"
              v-model="search"
              @change="onVisibleColumnsChange"
            >
            </list-visible-columns-and-filter>
          </v-row>
        </v-container>
      </v-col>
    </v-row>
    <v-card outlined>
      <v-data-table
        :items="items"
        :headers="filteredHeaders"
        show-select
        v-model="selectedRows"
        :search="search"
      >
        <template v-slot:item.pricing.totalBuy="{ item }">
          {{ item.pricing.totalBuy | priceLabeler }}
        </template>
        <template v-slot:item.price="{ item }">
          {{ item.price | priceLabeler }}</template
        >
        <template v-slot:item.pricing.discountCost="{ item }">
          {{ item.pricing.discountCost | priceLabeler }}
        </template>
        <template v-slot:item.pricing.circuitDiscountCost="{ item }">
          {{ item.pricing.circuitDiscountCost | priceLabeler }}
        </template>
        <template v-slot:item.pricing.discountSell="{ item }">
          {{ item.pricing.discountSell | priceLabeler }}
        </template>
        <template v-slot:item.pricing.circuitDiscountSell="{ item }">
          {{ item.pricing.circuitDiscountSell | priceLabeler }}
        </template>
        <template v-slot:item.serviceType="{ item }">
          <v-icon>{{ supplierTypeIcon(item.serviceType) }}</v-icon>
        </template>
        <template v-slot:item.totalCostInBase="{ item }">
          {{ item.totalCostInBase | priceLabeler }}
        </template>
        <template v-slot:item.totalSellInBase="{ item }">
          {{ item.totalSellInBase | priceLabeler }}
        </template>
        <template v-slot:item.margin="{ item }">
          {{ item.margin | priceLabeler }}
        </template>
        <template v-slot:item.markup="{ item }">
          {{ item.markup | priceLabeler }}
        </template>
        <template v-slot:item.supplierCommission="{ item }">
          {{ item.supplierCommission | priceLabeler }}
        </template>
        <template v-slot:item.pricing.currencySellExchangeRate="{ item }">
          <v-edit-dialog
            v-if="canEditPrices"
            :return-value.sync="item.pricing.currencySellExchangeRate"
            lazy
            large
            @save="onExchangeRateChange(item)"
            @open="onExchangeRateEditorOpen(item)"
          >
            {{ item.pricing.currencySellExchangeRate }}
            <template v-slot:input>
              <v-text-field
                v-model.number="item.pricing.currencySellExchangeRate"
                label="Edit"
                single-line
                type="number"
              ></v-text-field>
            </template>
          </v-edit-dialog>
          <span v-else>{{ item.pricing.currencySellExchangeRate }}</span>
        </template>
        <template v-slot:item.pricing.currencyBuyExchangeRate="{ item }">
          <v-edit-dialog
            v-if="canEditPrices"
            :return-value.sync="item.pricing.currencyBuyExchangeRate"
            lazy
            large
            @save="onExchangeRateChange(item)"
            @open="onExchangeRateEditorOpen(item)"
          >
            {{ item.pricing.currencyBuyExchangeRate }}
            <template v-slot:input>
              <v-text-field
                v-model.number="item.pricing.currencyBuyExchangeRate"
                label="Edit"
                single-line
                type="number"
              ></v-text-field>
            </template>
          </v-edit-dialog>
          <span v-else>{{ item.pricing.currencyBuyExchangeRate }}</span>
        </template>
        <template v-slot:item.startDate="{ item }">
          {{ item.startDate | formatDate }}
        </template>
        <template v-slot:item.adults="{ item }">
          {{ item.adults }}/{{ item.children }}/{{ item.infants }}
        </template>
        <template v-slot:item.duration="{ item }">
          {{ duration(item) }}
        </template>
        <template v-slot:item.isIncluded="{ item }">
          <v-checkbox
            disabled
            class="pa-0 ma-0"
            v-model="item.isIncluded"
            hide-details
          ></v-checkbox>
        </template>
        <template v-slot:item.supplierCommission="{ item }">
          {{ item.supplierCommissionValue | priceLabeler }}
        </template>

        <template v-slot:item.serviceName="{ item }">
          <v-row v-if="item.isExtra" align="center">
            <v-icon color="yellow darken-2">mdi-star</v-icon>
            {{ item.boardBasis }} ({{ item.serviceName }})
          </v-row>
          <span v-else>{{ item.serviceName }}</span>
        </template>

        <template v-slot:footer>
          <v-speed-dial
            v-show="selectedRows.length > 0 && canEditPrices"
            open-on-hover
            absolute
            direction="right"
            v-model="modifyServicesFab"
          >
            <template v-slot:activator>
              <v-btn v-model="modifyServicesFab" color="blue darken-2" dark fab>
                <v-icon v-if="modifyServicesFab">mdi-close</v-icon>
                <v-icon v-else>mdi-menu</v-icon>
              </v-btn>
            </template>

            <v-btn
              fab
              dark
              small
              color="teal accent-4"
              @click="onShowExchangeRateUpdater"
            >
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on">mdi-currency-usd</v-icon>
                </template>
                <span>Update Exchange Rate</span>
              </v-tooltip>
            </v-btn>
          </v-speed-dial>
          <v-layout justify-center pa-1>
            <span
              >Total Cost in Base:
              <strong>{{ totalCostInBase | priceLabeler }}</strong></span
            ><span
              >, Total Sell in Base:
              <strong>{{ totalSellInBase | priceLabeler }}</strong></span
            >
            <span
              >,&nbsp;Agent Net Commission:
              <strong>{{
                itinerary ? itinerary.profit.netCommission : 0 | priceLabeler
              }}</strong></span
            >
            <span
              >,&nbsp;TOMS Tax
              <strong>{{ totalToms | priceLabeler }}</strong></span
            >
            <span
              >,&nbsp;Net Profit:
              <strong>{{
                itinerary ? itinerary.profit.netProfit : 0 | priceLabeler
              }}</strong></span
            >
            <span
              >,&nbsp;Margin:
              <strong>{{
                itinerary ? itinerary.profit.margin : 0 | priceLabeler
              }}</strong></span
            >
            <span
              >,&nbsp;Markup:
              <strong>{{
                itinerary ? itinerary.profit.markup : 0 | priceLabeler
              }}</strong></span
            >
          </v-layout>
        </template>
      </v-data-table>
    </v-card>
    <exchange-rate-updater
      :show="showExchangeRateUpdater"
      @close="showExchangeRateUpdater = false"
      :items="selectedRows"
      @update-exchange-rates="onUpdateExchangeRates"
    />
    <v-row v-if="debug">
      <v-col
        ><code>{{ search }}</code></v-col
      >
    </v-row>
  </v-container>
</template>

<script>
import { getTotal, sortByDate } from "../../../../helpers";
import ListVisibleColumnsAndFilter from "../../shared/ListVisibleColumnsAndFilter.vue";
import ItineraryBuilderSettings from "./ItineraryBuilderSettings.vue";
import { SupplierTypeIconMixin } from "../../../../mixins/SupplierTypeIconMixin";
import EventBus from "../../../../store/event-bus";
import ExchangeRateUpdater from "./tools/ExchangeRateUpdater.vue";
const moment = require("moment");
export default {
  components: {
    ListVisibleColumnsAndFilter,
    ItineraryBuilderSettings,
    ExchangeRateUpdater,
  },
  name: "pricing-view",
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    itinerary: {
      type: Object,
      default: () => null,
    },
    search: {
      type: String,
      default: "",
    },
  },

  mixins: [SupplierTypeIconMixin],

  data() {
    return {
      debug: false,
      selectedRows: [],
      modifyServicesFab: false,
      headers: [
        {
          text: "Type",
          value: "serviceType",
          sortable: true,
          name: "serviceType",
        },
        {
          text: "From",
          value: "startDate",
          name: "startDate",
          sort: sortByDate,
        },
        {
          text: "Supplier",
          name: "supplierName",
          value: "supplierName",
        },
        {
          text: "Service",
          name: "serviceName",
          value: "serviceName",
        },
        {
          text: "HeadOffice",
          name: "headOffice",
          value: "headOffice",
        },
        {
          text: "Status",
          value: "status",
          name: "status",
        },
        {
          text: "A/Ch/I",
          value: "adults",
          name: "aci",
        },
        {
          text: "Qty",
          value: "qty",
          name: "qty",
        },
        {
          text: "Duration",
          value: "duration",
          name: "duration",
        },
        {
          text: "Cost Currency",
          value: "pricing.currencyBuy",
          name: "currencyBuy",
        },
        { text: "Cost", value: "pricing.totalBuy", name: "totalCost" },
        {
          text: "Discount (Cost)",
          value: "pricing.discountCost",
          name: "discountCost",
        },
        {
          text: "CDiscount (Cost)",
          value: "pricing.circuitDiscountCost",
          name: "circuitDiscountCost",
        },
        {
          text: "Cost ROE",
          value: "pricing.currencyBuyExchangeRate",
          name: "currencyBuyExchangeRate",
        },
        {
          text: "Cost in Base",
          value: "totalCostInBase",
          name: "totalCostInBase",
        },
        {
          text: "Sell Currency",
          value: "pricing.currencySell",
          name: "currency",
        },
        { text: "Sell", value: "price", name: "price" },
        {
          text: "Discount (Sell)",
          value: "pricing.discountSell",
          name: "discountSell",
        },
        {
          text: "CDiscount (Sell)",
          value: "pricing.circuitDiscountSell",
          name: "circuitDiscountSell",
        },
        {
          text: "Sell ROE",
          value: "pricing.currencySellExchangeRate",
          name: "currencySellExchangeRate",
        },
        {
          text: "Sell in Base",
          value: "totalSellInBase",
          name: "totalSellInBase",
        },
        { text: "Margin %", value: "margin", name: "margin" },
        { text: "Markup %", value: "markup", name: "markup" },
        {
          text: "Supplier Commission",
          value: "supplierCommission",
          name: "supplierCommission",
        },
        {
          text: "Agent Commission",
          value: "commission",
          name: "commission",
        },
        {
          text: "Business Model",
          value: "businessModel",
          name: "businessModel",
        },
        { text: "Included", value: "isIncluded", name: "isIncluded" },
      ],
      visibleColumns: this.$store.getters.localSettings(
        "itPricingVisibleColumns",
        [
          "startDate",
          "supplierName",
          "supplier.headOffice",
          "totalPrice",
          "serviceName",
          "headOffice",
          "status",
          "aci",
          "qty",
          "duration",
          "currencyBuy",
          "totalCost",
          "discountCost",
          "currencyBuyExchangeRate",
          "totalCostInBase",
          "currency",
          "price",
          "discountSell",
          "currencySellExchangeRate",
          "totalSellInBase",
        ]
      ),
      beforeChangeItem: null,
      showExchangeRateUpdater: false,
    };
  },

  computed: {
    canEditPrices() {
      return this.$store.getters.hasAccessTo("itineraries:edit_prices", true);
    },
    filteredHeaders() {
      // return this.headers.filter(
      //   (h) => this.visibleColumns.indexOf(h.name) > -1
      // );
      const hs = [];
      for (const headerName of this.visibleColumns.filter(
        (c) => c != "actions"
      )) {
        const h = this.headers.find((h) => h.name == headerName);
        if (h) {
          hs.push(h);
        }
      }
      const actionsHeader = this.headers.find((h) => h.name == "actions");
      if (actionsHeader) {
        hs.push(actionsHeader);
      }
      return hs;
    },
    totalSellInBase() {
      return getTotal(
        this.items.filter((item) => item.isIncluded),
        (item) => item.totalSellInBase
      );
    },
    totalCostInBase() {
      return getTotal(
        this.items.filter((item) => item.isIncluded),
        (item) => item.totalCostInBase
      );
    },
    totalToms() {
      return getTotal(
        this.items.filter((item) => item.isIncluded),
        (item) => item.toms
      );
    },
  },

  methods: {
    onShowExchangeRateUpdater(payload) {
      this.showExchangeRateUpdater = true;
    },
    onUpdateExchangeRates(payload) {
      this.selectedRows = [];
      this.showExchangeRateUpdater = false;
      this.$emit("update-exchange-rates", payload);
    },
    onVisibleColumnsChange(columns) {
      //console.log("columns changed", columns);
      this.visibleColumns = [...columns];
      this.$store.dispatch("updateLocalSettings", {
        itPricingVisibleColumns: [...columns],
      });
    },
    duration(item) {
      let start = moment(item.startDate).startOf("day");
      let end = moment(item.endDate).startOf("day");
      if (start.isValid() && end.isValid()) {
        let d = end.diff(start, "days");
        let addDay =
          item.serviceType != "ACCOMMODATION" || item.isExtra ? 1 : 0;
        //let addDay = 1;
        if (item.serviceType == "TRANSPORT") {
          //http://tracker.globetrack.ie/issues/5905
          // only if both pickup and dropoff times are set
          if (
            item.remarks &&
            item.remarks.pickupTime &&
            item.remarks.pickupTime != "" &&
            item.remarks.dropoffTime &&
            item.remarks.dropoffTime != ""
          ) {
            const pickupTime =
              item.remarks &&
              item.remarks.pickupTime &&
              item.remarks.pickupTime != ""
                ? item.remarks.pickupTime
                : "00:00";
            const dropoffTime =
              item.remarks &&
              item.remarks.dropoffTime &&
              item.remarks.dropoffTime != ""
                ? item.remarks.dropoffTime
                : "00:01";
            addDay =
              Number(dropoffTime.replace(/\D/, "")) >
              Number(pickupTime.replace(/\D/, ""))
                ? 1
                : 0;
          }
        }
        return d == 0 ? 1 : d + addDay;
      }
      return 1;
    },
    onExchangeRateChange(item) {
      item.customPricing = true;
      if (item.pricing.logs.length == 0 && this.beforeChangeItem) {
        item.pricing.logs.push(this.getLog(this.beforeChangeItem));
      }
      this.addLog(item);

      //update totals without recalc #7625
      item.totalCostInBase =
        Number(item.pricing.totalBuy) /
        Number(item.pricing.currencyBuyExchangeRate);
      item.totalSellInBase =
        Number(item.price) / Number(item.pricing.currencySellExchangeRate);
      if (item.isExtra) {
        const editedExtra = item.extras.find((e) => e.id == item.extraId);
        editedExtra.currencySellExchangeRate =
          item.pricing.currencySellExchangeRate;
        editedExtra.currencyBuyExchangeRate =
          item.pricing.currencyBuyExchangeRate;
        editedExtra.totalCostInBase =
          Number(editedExtra.totalBuy) /
          Number(editedExtra.currencyBuyExchangeRate);
        editedExtra.totalSellInBase =
          Number(editedExtra.totalSell) /
          Number(editedExtra.currencySellExchangeRate);

        //const parentItem = this.items.find((i) => i.id == item.itemId);
        // if (parentItem) {
        //   parentItem.extras = parentItem.extras.filter(
        //     (e) => e.id != item.extraId
        //   );
        // }
      }
      EventBus.$emit("iti-items-refresh");
      //this.$emit("recalculate");
    },
    onExchangeRateEditorOpen(item) {
      if (item.pricing && item.pricing.logs.length == 0) {
        this.beforeChangeItem = JSON.parse(JSON.stringify(item));
      } else {
        this.beforeChangeItem = null;
      }
    },
    getLog(item) {
      const {
        rateTypeCode,
        itemBuy,
        itemSell,
        adultBuy,
        adultSell,
        childBuy,
        childSell,
        infantBuy,
        infantSell,
        currencyBuy,
        currencySell,
        currencyBuyExchangeRate,
        currencySellExchangeRate,
      } = item.pricing;
      const supplierCommission = item.supplierCommission;
      return {
        date: moment().format("YYYY-MM-DD"),
        time: moment().format("HH:mm"),
        username: this.$store.getters.userConfig.username,
        rateType: rateTypeCode,
        itemBuy,
        itemSell,
        adultBuy,
        adultSell,
        childBuy,
        childSell,
        infantBuy,
        infantSell,
        currencyBuy,
        currencySell,
        currencyBuyExchangeRate,
        currencySellExchangeRate,
        supplierCommission,
      };
    },
    addLog(item) {
      item.pricing.logs.push(this.getLog(item));
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/gt/itineraries/builder/PricingView.vue